import React, { Component } from "react";
import { Button, ButtonGroup, ProgressBar } from "react-bootstrap";

import BrandHeader from "../../components/BrandHeader";
import IconButton from "../../components/IconButton";
import JoinQRCodeButton from "../../components/JoinQRCodeButton";
import LeaderboardSidebar from "../../components/LeaderboardSidebar";
import PlayerAvatarStrip from "../../components/PlayerAvatarStrip";
import Timer from "../../components/Timer";
import { closeRoom } from "../../connection/config";
import { toLetter } from "../../utilities";
import { ONE_HUNDRED } from "../../utilities/constants";

import ArrowForward from "../../assets/icons/arrow_forward.svg";
import CheckBox from "../../assets/icons/check_box.svg";
import Close from "../../assets/icons/close.svg";

import "../../assets/icons/material-ui-icon.css";
import "./Question.css";

export const TAB_REVEAL_ANSWER = 1;
export const TAB_ANSWER_STATS = 2;

const Phase = {
    GUESSING: 1,
    REVEALING: 2,
};

const ButtonStyle = {
    ACTIVE: {
        variant: "secondary",
        disabled: false,
    },
    DISABLED: {
        variant: "secondary",
        disabled: true,
    },
};

class Question extends Component {

    constructor(props) {
        super(props);
        this.state = {
            tab: 0,
            autoAdvanceSecondsLeft: null
        };
        this.timer = React.createRef();
        this.autoAdvanceInterval = null;
        this.onNextButton = this.onNextButton.bind(this);
        this.endQuiz = this.endQuiz.bind(this);
        this.onTimerStop = this.onTimerStop.bind(this);
        this.renderAnswer = this.renderAnswer.bind(this);
    }

    componentDidMount() {
        this.updateAutoAdvanceCountdown();
    }

    componentDidUpdate(prevProps) {
        // Start/update countdown when autoAdvanceAt changes
        if (prevProps.autoAdvanceAt !== this.props.autoAdvanceAt) {
            this.updateAutoAdvanceCountdown();
        }
    }

    componentWillUnmount() {
        this.clearAutoAdvanceInterval();
    }

    clearAutoAdvanceInterval() {
        if (this.autoAdvanceInterval) {
            clearInterval(this.autoAdvanceInterval);
            this.autoAdvanceInterval = null;
        }
    }

    updateAutoAdvanceCountdown() {
        this.clearAutoAdvanceInterval();

        if (!this.props.autoAdvanceAt) {
            this.setState({ autoAdvanceSecondsLeft: null });
            return;
        }

        const updateSeconds = () => {
            const now = Date.now();
            const remaining = Math.max(0, Math.ceil((this.props.autoAdvanceAt - now) / 1000));

            this.setState({ autoAdvanceSecondsLeft: remaining });

            if (remaining <= 0) {
                // Use onNextButton() to ensure timer is restarted (same as manual click)
                this.onNextButton();
            }
        };

        updateSeconds();
        this.autoAdvanceInterval = setInterval(updateSeconds, 100);
    }

    getPhase() {
        if (this.props.questionIsOpen) {
            return Phase.GUESSING;
        } else {
            return Phase.REVEALING;
        }
    }

    isTimerEnabled() {
        return 0 < this.props.game.hostingRoom.timeLimit;
    }

    startTimer() {
        if (this.isTimerEnabled() && this.timer && this.timer.current) {
            this.timer.current.startTimer();
        }
    }

    stopTimer() {
        if (this.isTimerEnabled() && this.timer && this.timer.current) {
            this.timer.current.stopTimer();
        }
    }

    onTimerStop() {
        if (Phase.GUESSING === this.getPhase()) {
            this.props.onRevealAnswer();
        }
    }

    correctGreenBox(answer) {
        const isRevealAnswerTab = this.props.questionTab === TAB_REVEAL_ANSWER;
        const isCorrectAnswer = this.props.question.correct === answer;
        const mustRevealAnswer = (isRevealAnswerTab || this.props.revealAnswer) && isCorrectAnswer;
        return mustRevealAnswer ? " question-answer-correct" : "";
    }

    StatProgressBar(answer) {
        let value = 0;
        if (this.props.answerCount !== 0 && this.props.answerStats) {
            value = Math.round(this.props.answerStats[answer] * ONE_HUNDRED / this.props.answerCount);
        }
        if ((this.props.questionTab === TAB_ANSWER_STATS || this.props.revealStats)) {
            return (<ProgressBar now={value} label={value + "%"} className="question-progress" />);
        } else {
            return false;
        }
    }

    renderAnswer(answer, index) {
        return (
            <div className={"question-answer" + this.correctGreenBox(index)} key={index}>
                <div className="question-answer-letter">{toLetter(index)}</div>
                <div className="question-answer-text">{answer}</div>
                {this.StatProgressBar(index)}
            </div>
        );
    }

    renderTimer() {
        if (this.isTimerEnabled()) {
            // During reveal phase with auto-advance, show auto-advance countdown
            // Otherwise show the answer countdown (or 0:00 when stopped)
            const phase = this.getPhase();
            const overrideSeconds = (phase === Phase.REVEALING && this.state.autoAdvanceSecondsLeft !== null)
                ? this.state.autoAdvanceSecondsLeft
                : null;

            return (
                <div className="question-timer-container">
                    <Timer
                        ref={this.timer}
                        timeLimit={this.props.game.hostingRoom.timeLimit}
                        onTimerStop={this.onTimerStop}
                        overrideSeconds={overrideSeconds}
                    />
                </div>
            );
        } else {
            return null;
        }
    }

    renderControlButton(icon, label1, label2, style, onClick) {
        return (
            <Button variant={style.variant} disabled={style.disabled} onClick={onClick}>
                <img src={icon} className="material-ui-icon" alt={`${label1} ${label2}`.trim()} />
                <br />{label1}
                <br />{label2}
            </Button>
        );
    }

    renderRevealAnswerButton() {
        // Active during GUESSING (to stop + reveal) or REVEALING (if not already revealed)
        // Disabled if already revealed
        const alreadyRevealed = this.props.revealAnswer;
        const style = alreadyRevealed ? ButtonStyle.DISABLED : ButtonStyle.ACTIVE;
        const onClick = () => {
            this.stopTimer();
            this.props.onRevealAnswer();
        };
        return this.renderControlButton(CheckBox, "Reveal", "answer", style, onClick);
    }

    renderNextButton() {
        const phase = this.getPhase();
        const style = Phase.GUESSING === phase ? ButtonStyle.DISABLED : ButtonStyle.ACTIVE;
        if (!this.props.isLastQuestion) {
            return this.renderControlButton(ArrowForward, "Next", "question", style, this.onNextButton);
        } else {
            return this.renderControlButton(ArrowForward, "Leader-", "board", style, this.endQuiz);
        }
    }

    endQuiz() {
        // Include hostToken for authorization
        this.props.socket.emit(closeRoom, this.props.game.hostingRoom.roomCode, this.props.getHostToken());
    }

    onNextButton() {
        // Clear auto-advance interval immediately (manual override)
        this.clearAutoAdvanceInterval();
        this.setState({ autoAdvanceSecondsLeft: null });

        this.startTimer();
        this.props.nextButton();
    }

    onQuitClick = () => {
        this.props.socket.emit(closeRoom, this.props.game.hostingRoom.roomCode, this.props.getHostToken());
    };

    render() {
        const answerStatsClass = this.props?.revealStats ? "answer-stats-visible" : "";
        const hasImageClass = this.props?.question?.imageUrl ? "has-image" : "";
        const className = [answerStatsClass, hasImageClass].filter(Boolean).join(" ");

        return (
            <div className="host-question-view-wrapper">
            <div className="host-question-view">
                {/* TopBar */}
                <div className="hqv-top-bar">
                    <div className="hqv-top-bar__left">
                        <IconButton
                            icon={Close}
                            label="End quiz"
                            containerClassName="hqv-quit-button"
                            onClick={this.onQuitClick}
                        />
                    </div>
                    <div className="hqv-top-bar__center">
                        <BrandHeader size="compact" variant="host" />
                    </div>
                    <div className="hqv-top-bar__right">
                        <JoinQRCodeButton roomCode={this.props.game.hostingRoom.roomCode} size={72} />
                    </div>
                </div>

                {/* MetaRow */}
                <div className="hqv-meta-row">
                    <div className="hqv-meta-row__avatars">
                        <span className="hqv-meta-row__label">Players</span>
                        <PlayerAvatarStrip players={this.props.players} maxVisible={30} size={32} />
                    </div>
                    <div className="hqv-meta-row__progress">
                        <div className="hqv-question-progress">
                            Question {this.props.questionIndex + 1} / {this.props.lastIndexNumber}
                        </div>
                        {this.renderTimer()}
                    </div>
                </div>

                {/* MainContent */}
                <div className={`hqv-main-content ${className}`}>
                    {/* LeaderboardColumn */}
                    <div className="hqv-leaderboard-column">
                        <LeaderboardSidebar data={this.props.generalRanking} />
                    </div>

                    {/* QuestionColumn */}
                    <div className="hqv-question-column">
                        {/* QuestionBox */}
                        <div className="hqv-question-box">
                            <div className="hqv-question-text">
                                {this.props.question.question}
                            </div>
                            {this.props.question.imageUrl && (
                                <div className="hqv-question-image">
                                    <img src={this.props.question.imageUrl} alt="An image that relates to the question" />
                                </div>
                            )}
                        </div>

                        {/* AnswersBox */}
                        <div className="hqv-answers-box">
                            {this.props.question.answers.map(this.renderAnswer)}
                        </div>
                    </div>
                </div>

                {/* BottomActionBar */}
                <div className="hqv-bottom-action-bar">
                    <ButtonGroup className="hqv-action-button-group">
                        {this.renderRevealAnswerButton()}
                    </ButtonGroup>
                    <ButtonGroup className="hqv-action-button-group">
                        {this.renderNextButton()}
                    </ButtonGroup>
                </div>
            </div>
            </div>
        );
    }
}

export default Question;
